import { useSyncExternalStore } from "react";

/**
 * Which signal, if any, is open in the detail modal.
 *
 * A module singleton rather than a context: the modal is mounted once at the
 * app root and opened from a feed row several levels down. A provider would
 * mean threading state through every component between the two, and there is
 * exactly one modal.
 */

export interface SignalModalState {
  open: boolean;
  signalId: string | null;
}

const CLOSED: SignalModalState = { open: false, signalId: null };

let state: SignalModalState = CLOSED;
/** Where focus was when the modal opened, so closing puts it back. */
let returnFocusTo: HTMLElement | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function openSignalModal(id: string, returnFocus?: HTMLElement | null): void {
  // Re-opening from inside the modal — following a nearby signal — must not
  // lose the element that had focus before the first open.
  if (!state.open) returnFocusTo = returnFocus ?? null;
  state = { open: true, signalId: id };
  emit();
}

export function closeSignalModal(): void {
  state = CLOSED;
  const target = returnFocusTo;
  returnFocusTo = null;
  emit();
  // After the emit, so the modal has unmounted and cannot steal it back.
  target?.focus();
}

export function useSignalModal(): SignalModalState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): SignalModalState {
  return state;
}

/** Test-only: nothing in the app resets a singleton mid-session. */
export function resetSignalModal(): void {
  state = CLOSED;
  returnFocusTo = null;
  emit();
}
